// Component: RollingNumber.jsx
// Purpose: Per-digit rolling counter for formatted numeric strings
//   ("+₹12,847", "1,492.65"). On value change each DIGIT rolls vertically to its
//   new glyph through a stacked 0-9 column; non-digit glyphs (₹ , . + - %)
//   swap instantly; digits added/removed at the leading edge fade+slide in/out.
//   No vertical layout shift: every digit lives in a fixed-height overflow-hidden
//   cell and the whole thing inherits the caller's tabular-nums typography
//   (drop it inside a .ds-num-* / .ds-mono value and it takes that font/size).
//
//   Odometer identity: cells are keyed by position FROM THE RIGHT, so as the
//   integer part grows the existing digits keep identity (only the new leftmost
//   glyphs animate in) and any decimals stay put.
//
//   Motion: SPRING_SNAPPY, ≤240ms feel. Reduced-motion → instant swap (no roll,
//   no enter/exit slide) via framer's useReducedMotion.
//
// Usage:
//   <RollingNumber value="+₹12,847" />
//   <div className="ds-num-hero"><RollingNumber value={formatted} /></div>

import React from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { cx } from "./cx";
import { SPRING_SNAPPY } from "./motion";

const GLYPHS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

// Split a formatted string into cells keyed by position-from-right (odometer
// identity — see header). isDigit gates rolling vs instant swap.
function toCells(value) {
  const chars = String(value ?? "").split("");
  const n = chars.length;
  return chars.map((char, i) => ({
    key: n - 1 - i,
    char,
    isDigit: char >= "0" && char <= "9",
  }));
}

// A single rolling digit: a one-glyph-tall window over a stacked 0-9 column.
// The visible spacer ("0") sizes the cell to exactly one glyph (line-height and
// tabular width inherited); the absolutely-positioned column is translated by
// -digit * 10% of its own (10-glyph) height, so the maths is unit-free.
function RollingDigit({ digit, reduced }) {
  const roll = reduced ? { duration: 0 } : SPRING_SNAPPY;
  return (
    <motion.span
      className="ds-rolling-digit"
      aria-hidden="true"
      initial={{ opacity: 0, y: "45%" }}
      animate={{ opacity: 1, y: "0%" }}
      exit={{ opacity: 0, y: "-45%" }}
      transition={roll}
    >
      <span className="ds-rolling-spacer">0</span>
      <motion.span
        className="ds-rolling-col"
        animate={{ y: `${-digit * 10}%` }}
        transition={roll}
      >
        {GLYPHS.map((g) => (
          <span key={g} className="ds-rolling-glyph">
            {g}
          </span>
        ))}
      </motion.span>
    </motion.span>
  );
}

export function RollingNumber({ value, className, ...props }) {
  const reduced = useReducedMotion();
  const cells = toCells(value);

  return (
    <span className={cx("ds-rolling", className)} aria-label={String(value ?? "")} {...props}>
      {/* initial={false} → the value present on first mount doesn't animate in;
          only subsequent digit additions/removals get the enter/exit slide. */}
      <AnimatePresence initial={false}>
        {cells.map((c) =>
          c.isDigit ? (
            <RollingDigit key={c.key} digit={Number(c.char)} reduced={reduced} />
          ) : (
            <span key={c.key} className="ds-rolling-static" aria-hidden="true">
              {c.char}
            </span>
          )
        )}
      </AnimatePresence>
    </span>
  );
}

export default RollingNumber;
